import React, { useState } from 'react';
import { conditionTraits, ConditionTrait } from './conditionText';
import { formatLift, liftBarPercent } from './nodeLift';
import { nodeSegment, segmentColor } from './nodeSegment';
import { EnsembleThreshold } from '../../../models/ensembling';
import { Threshold } from '../../../models/ModelPerformances';
import './treeNodes.css';

// Best fit first, matching how the performance chart stacks them.
const LEGEND_SEGMENTS: Threshold[] = ['very good', 'good', 'medium', 'low'];

/**
 * One condition with the field it tests set apart from the value it tests for.
 * "company type other than Private" is three different kinds of word and read as
 * one flat run, so which half was the field and which was the value had to be
 * inferred from knowing the data. The parser has both exactly, from the AST.
 */
export function TraitText({ trait }: { trait: ConditionTrait }) {
  return (
    <>
      {trait.parts.map((part, i) => (
        <React.Fragment key={`${part.kind}-${part.text}-${i}`}>
          {i > 0 && ' '}
          <span className={`tpart tpart--${part.kind}`}>{part.text}</span>
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * A node's condition as one chip per requirement. Collapsed past `max` chips so a
 * deep node does not push everything else off the card, expandable in place.
 */
export function NodeTraits({
  condition,
  max = 4,
}: {
  condition?: string | null;
  max?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const traits = conditionTraits(condition);
  if (!traits.length) return null;

  const hidden = expanded ? 0 : Math.max(0, traits.length - max);
  const shown = hidden ? traits.slice(0, max) : traits;

  return (
    <div className="tnode-traits">
      <span className="tnode-traits-label">Traits</span>
      {shown.map((trait, i) => (
        <React.Fragment key={`${trait.text}-${i}`}>
          {i > 0 && <span className="tnode-and">and</span>}
          <span
            className={`tnode-trait ${
              trait.unparsed ? 'tnode-trait--unparsed' : ''
            }`}
          >
            <TraitText trait={trait} />
          </span>
        </React.Fragment>
      ))}
      {(hidden > 0 || expanded) && (
        <button
          type="button"
          className="tnode-toggle"
          onClick={() => setExpanded((open) => !open)}
        >
          {hidden > 0 ? `+${hidden} more` : 'show less'}
        </button>
      )}
    </div>
  );
}

/** Lift as a number plus a bar, so a list of nodes can be scanned by shape. */
export function LiftBar({ lift }: { lift: number | null }) {
  if (lift === null) return null;
  const under = lift < 1;
  return (
    <span className="tnode-lift">
      <span
        className={`tnode-lift-value ${under ? 'tnode-lift-value--under' : ''}`}
      >
        {formatLift(lift)}
      </span>
      <span className="tnode-lift-bar">
        <span
          className={`tnode-lift-fill ${under ? 'tnode-lift-fill--under' : ''}`}
          style={{ width: `${liftBarPercent(lift)}%` }}
        />
      </span>
    </span>
  );
}

/**
 * Legend for the tree's node colours. Reads the same CHART_COLORS the model
 * performance chart uses, so the two cannot fall out of step, and falls back to
 * the old gradient wording when the model has no thresholds set.
 */
export function SegmentLegend({
  thresholds,
  fallback,
}: {
  thresholds?: EnsembleThreshold;
  fallback?: React.ReactNode;
}) {
  // Same guard as the node colouring: an unconfigured model reads as all-zero,
  // and nothing is coloured by segment, so the legend must not claim otherwise.
  if (!nodeSegment(1, thresholds)) return <>{fallback ?? null}</>;
  return (
    <span className="tnode-legend">
      {LEGEND_SEGMENTS.map((segment) => (
        <span className="tnode-legend-item" key={segment}>
          <span
            className="tnode-legend-dot"
            style={{ background: segmentColor(segment) }}
          />
          {segment}
        </span>
      ))}
    </span>
  );
}

/** The node's fit segment as a coloured dot, so a list of nodes reads by band. */
export function SegmentBadge({
  conversionRate,
  thresholds,
}: {
  conversionRate: number;
  thresholds?: EnsembleThreshold;
}) {
  const segment = nodeSegment(conversionRate, thresholds);
  if (!segment) return null;
  return (
    <span className="tnode-badge">
      <span
        className="tnode-legend-dot"
        style={{ background: segmentColor(segment) }}
      />
      {segment}
    </span>
  );
}
